import type { ModuleManifest } from '../types.js'

/**
 * The CRM screens that shipped with the Shadcn Studio template — companies,
 * people, opportunities, tasks, notes, workflows and their dashboards.
 *
 * ! Its data still comes from `apps/admin/src/fake-db`, NOT from the API. It is
 * ! carried as a real module so the registry has a second tenant from day one
 * ! and so the screens stay reachable while their data is ported. Turning it off
 * ! in Settings ▸ Modules hides it; deleting `src/fake-db`, `src/views/apps/*`
 * ! and this manifest removes it.
 */
export const crmModule = {
  key: 'crm',
  name: 'CRM (жишиг загвар)',
  description:
    'Shadcn Studio загвараас ирсэн дэлгэцүүд. АМЬД БОЛОМЖ БИШ — шинэ дэлгэц ' +
    'бичихэд хуулж авах ЛАВЛАГАА. Өгөгдөл нь fake-db дээр, API-д холбогдоогүй.',

  /*
   * ! ШИНЭ суулгацад УНТРААЛТАЙ ирнэ.
   *
   * Энэ модуль нь ажиллах боломж биш, ЗАГВАРЫН САН: `people`, `companies`,
   * `opportunities`, `tasks`, `notes`, `workflows` дэлгэцүүд нь хүснэгт, панел,
   * шүүлтүүрийн бүтцийг харуулах жишээ болж репод үлдэнэ. Шинэ модуль бичихэд
   * эндээс хуулж эхэлнэ (`11 §Шинэ дэлгэц`).
   *
   * Асаалттай байвал хуурамч өгөгдөлтэй дэлгэц цэсэнд гарч, бодит модулиудтай
   * зэрэгцэн жинхэнэ мэт харагдана.
   *
   * ! Асаах бол Тохиргоо ▸ Модулиуд — тэр сэлгүүр seed-д дарагдахгүй.
   */
  enabledByDefault: false,
  version: '1.0.0',
  icon: 'TargetIcon',

  permissions: [
    { resource: 'crm.records', action: 'read', description: 'CRM бүртгэл харах' },
    { resource: 'crm.records', action: 'create', description: 'CRM бүртгэл үүсгэх' },
    { resource: 'crm.records', action: 'update', description: 'CRM бүртгэл засах' },
    { resource: 'crm.records', action: 'delete', description: 'CRM бүртгэл устгах' },
  ],

  navigation: [
    {
      label: 'CRM самбар',
      items: [
        {
          label: 'Analytics',
          href: '/dashboards/analytics',
          icon: 'ChartLineIcon',
          iconClassName: 'text-indigo-600 dark:text-indigo-400',
          permission: 'crm.records:read',
        },
        {
          label: 'CRM Overview',
          href: '/dashboards/crm',
          icon: 'LayoutDashboardIcon',
          iconClassName: 'text-sky-600 dark:text-sky-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Sales',
          href: '/dashboards/sales',
          icon: 'TrendingUpIcon',
          iconClassName: 'text-emerald-600 dark:text-emerald-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Leads',
          href: '/dashboards/leads',
          icon: 'FilterIcon',
          iconClassName: 'text-violet-600 dark:text-violet-400',
          permission: 'crm.records:read',
        },
      ],
    },
    {
      label: 'CRM',
      items: [
        {
          label: 'People',
          href: '/people',
          icon: 'UsersIcon',
          iconClassName: 'text-indigo-600 dark:text-indigo-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Companies',
          href: '/companies',
          icon: 'Building2Icon',
          iconClassName: 'text-blue-600 dark:text-blue-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Opportunities',
          href: '/opportunities',
          icon: 'TargetIcon',
          iconClassName: 'text-rose-600 dark:text-rose-400',
          permission: 'crm.records:read',
        },
      ],
    },
    {
      label: 'Ажлын талбар',
      items: [
        {
          label: 'Tasks',
          href: '/tasks',
          icon: 'ListTodoIcon',
          iconClassName: 'text-emerald-600 dark:text-emerald-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Notes',
          href: '/notes',
          icon: 'StickyNoteIcon',
          iconClassName: 'text-teal-600 dark:text-teal-400',
          permission: 'crm.records:read',
        },
        {
          label: 'Workflows',
          icon: 'WorkflowIcon',
          iconClassName: 'text-amber-600 dark:text-amber-400',
          permission: 'crm.records:read',
          children: [
            { label: 'Workflows', href: '/workflows' },
            { label: 'Workflow Runs', href: '/workflow-runs' },
            { label: 'Workflow Versions', href: '/workflow-versions' },
          ],
        },
      ],
    },
  ],

  // No Refine resources: these screens read `src/fake-db` through zustand rather
  // than a data provider. They gain resources when their data moves to the API.
  resources: [],
} as const satisfies ModuleManifest
